<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { useAlert } from 'dashboard/composables';
import {
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper';

const props = defineProps({ conversation: { type: Object, required: true } });
const store = useStore();
const { t } = useI18n();
const busy = ref(false);
const pushEnabled = ref(false);
const enablePush = () => {
  verifyServiceWorkerExistence(() =>
    requestPushPermissions({
      onSuccess: () => {
        pushEnabled.value = true;
      },
    })
  );
};
const handoff = computed(
  () => props.conversation.additional_attributes?.assignment_handoff
);
const active = computed(
  () =>
    handoff.value &&
    handoff.value.to_id === props.conversation.meta?.assignee?.id
);
const label = computed(() => {
  const parameters = {
    sender: handoff.value?.from_name,
    recipient: handoff.value?.to_name,
  };
  return handoff.value?.state === 'accepted'
    ? t('CONVERSATION.TRANSFER.ACCEPTED', parameters)
    : t('CONVERSATION.TRANSFER.PENDING', parameters);
});
const mine = computed(
  () => handoff.value?.to_id === store.getters.getCurrentUserID
);
const accept = async () => {
  busy.value = true;
  try {
    await ConversationAPI.acceptTransfer(
      props.conversation.id,
      handoff.value.id
    );
    await store.dispatch('getConversation', props.conversation.id);
  } catch {
    useAlert(t('CONVERSATION.TRANSFER.FAILED'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div
    v-if="active"
    class="flex items-center justify-between gap-3 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
  >
    <span>{{ label }}</span>
    <button
      v-if="mine && !pushEnabled"
      type="button"
      class="shrink-0 rounded px-2 py-1 text-n-blue-11 hover:bg-n-blue-3"
      @click="enablePush"
    >
      {{ t('CONVERSATION.TRANSFER.ENABLE_PUSH') }}
    </button>
    <button
      v-if="mine && handoff.state !== 'accepted'"
      type="button"
      class="shrink-0 rounded bg-n-amber-5 px-3 py-1 font-medium disabled:opacity-50"
      :disabled="busy"
      @click="accept"
    >
      {{ t('CONVERSATION.TRANSFER.ACCEPT') }}
    </button>
  </div>
</template>
